import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, StatusIcon, getAppointmentStatus } from "components";
import { COMMUNICATION_CHANNELS, COMMUNICATION_EVENTS, COMMUNICATION_RECEIVER_STATUSES, STATUS_IDENTIFIERS } from "models";
import "modules/AppointmentDetails/components/StatusBarIcon/StatusBarIcon.scss";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { formatDate, isSameDayHelper } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { COMMUNICATION_STATUSES, CommunicationEvent, CommunicationReceiver } from "models";

type StatusIconProps = {
  status: STATUS_IDENTIFIERS;
  active?: boolean;
  name?: string;
  timestamp?: string | null;
  disabled?: boolean;
  color?: string;
  isMechanicState?: boolean;
  onClick?: () => void;
  customcomStatus?: COMMUNICATION_STATUSES | null;
  checkPausedAt?: string | null;
  communicationEvents?: CommunicationEvent[];
};

const SENT_COMMUNICATION_EVENTS = [
  COMMUNICATION_EVENTS.OnlineCheckInSent,
  COMMUNICATION_EVENTS.DiagnoseOverviewSent,
  COMMUNICATION_EVENTS.DiagnoseOverviewResent,
  COMMUNICATION_EVENTS.RepairOverviewSent,
  COMMUNICATION_EVENTS.RepairOverviewResent
];

const ANSWERED_COMMUNICATION_EVENTS = [COMMUNICATION_EVENTS.OnlineCheckInAnswered, COMMUNICATION_EVENTS.DiagnoseOverviewAnswered];

const getCommunicationEventLabel = (type: COMMUNICATION_EVENTS, t: TFunction) => {
  switch (type) {
    case COMMUNICATION_EVENTS.OnlineCheckInSent:
      return t("v8_online_check_in_sent", "Online check-in sent");
    case COMMUNICATION_EVENTS.OnlineCheckInOpened:
      return t("v8_online_check_in_opened", "Online check-in opened");
    case COMMUNICATION_EVENTS.OnlineCheckInAnswered:
      return t("v8_online_check_in_answered", "Online check-in answered");
    case COMMUNICATION_EVENTS.DiagnoseOverviewSent:
      return t("v8_diagnose_overview_sent", "Diagnose overview sent");
    case COMMUNICATION_EVENTS.DiagnoseOverviewOpened:
      return t("v8_diagnose_overview_opened", "Diagnose overview opened");
    case COMMUNICATION_EVENTS.DiagnoseOverviewAnswered:
      return t("v8_diagnose_overview_answered", "Diagnose overview answered");
    case COMMUNICATION_EVENTS.DiagnoseOverviewResent:
      return t("v8_diagnose_overview_resent", "Diagnose overview resent");
    case COMMUNICATION_EVENTS.RepairOverviewSent:
      return t("v8_repair_overview_sent", "Repair overview sent");
    case COMMUNICATION_EVENTS.RepairOverviewResent:
      return t("v8_repair_overview_resent", "Repair overview resent");
    case COMMUNICATION_EVENTS.RepairOverviewOpened:
      return t("v8_repair_overview_opened", "Repair overview opened");
    default:
      return "";
  }
};

const getChannelName = (channel: COMMUNICATION_CHANNELS, t: TFunction) => {
  if (channel === COMMUNICATION_CHANNELS.Email) return t("v8_email", "Email");
  if (channel === COMMUNICATION_CHANNELS.SMS) return t("v8_sms", "SMS");
  if (channel === COMMUNICATION_CHANNELS.WhatsApp) return t("v8_whatsapp_message", "WhatsApp message");
  return "";
};

const getReceiverStatusText = (receiver: CommunicationReceiver, t: TFunction) => {
  const channel = getChannelName(receiver.channel, t);
  if (receiver.status === COMMUNICATION_RECEIVER_STATUSES.Pending) return `${channel} ${t("v8_is_pending", "is pending")}`;
  if (receiver.status === COMMUNICATION_RECEIVER_STATUSES.Success) return `${channel} ${t("v8_is_sent", "is sent")}`;
  return `${channel} ${t("v8_has_failed", "has failed")}`;
};

const getReceiverStatusIcon = (status: COMMUNICATION_RECEIVER_STATUSES) => {
  if (status === COMMUNICATION_RECEIVER_STATUSES.Success) return "check";
  if (status === COMMUNICATION_RECEIVER_STATUSES.Failed) return "circle exclamation";
  return "hourglass";
};

const getDisplayedEvents = (events: CommunicationEvent[] = []) => {
  const sentEvent = events.findLast(event => SENT_COMMUNICATION_EVENTS.includes(event.type)) ?? events.at(0);
  const answeredEvent = events.findLast(event => ANSWERED_COMMUNICATION_EVENTS.includes(event.type));
  const labelEvent = sentEvent && answeredEvent && answeredEvent.id > sentEvent.id ? answeredEvent : events.at(-1);

  return { sentEvent, labelEvent };
};

export const StatusBarIcon = ({
  status: statusIdentifier,
  active,
  disabled,
  onClick,
  isMechanicState = false,
  name,
  timestamp,
  customcomStatus,
  checkPausedAt,
  communicationEvents
}: StatusIconProps) => {
  const { t } = useTranslation();

  const selectedLocation = useAppointmentLocation();

  const status = getAppointmentStatus(statusIdentifier, selectedLocation?.statuses);
  const iconColor = (disabled && !active) || isMechanicState ? "#a1a1a1" : "#000000";
  let backgroundColor = "#ffffff";

  if (active && status.color) backgroundColor = status.color;
  else if (isMechanicState) backgroundColor = "#ffffff";
  else if (disabled) backgroundColor = "#E6E6E6";

  const borderColor = isMechanicState ? "#B4B4B4" : !active && !disabled ? "#000000" : backgroundColor;

  const { sentEvent, labelEvent } = getDisplayedEvents(communicationEvents);
  const receivers = sentEvent?.receivers ?? [];
  const recipientName = [sentEvent?.customer?.title, sentEvent?.customer?.firstname, sentEvent?.customer?.surname].filter(Boolean).join(" ");
  const eventLabel = labelEvent ? getCommunicationEventLabel(labelEvent.type, t) : "";
  const showCommunicationEvent = labelEvent && (active || [STATUS_IDENTIFIERS.CarInShop, STATUS_IDENTIFIERS.CarOutOfShop].includes(statusIdentifier));

  return (
    <Popup
      wide
      content={
        <div className="StatusIcon-content">
          {timestamp || name || showCommunicationEvent ? (
            <div className="detail">
              <div className="grid-container">
                <div className="left-section">
                  <StatusIcon status={status.identifier} />
                </div>

                <div className="right-section">
                  <div className="status-time">
                    <span>{timestamp && formatDate(timestamp, isSameDayHelper(timestamp) ? DATE_FORMATS_FNS.hoursMinutes : DATE_FORMATS_FNS.dateMonthYearTime)}</span>
                    <span>{status.name}</span>
                  </div>
                  <span>{name}</span>
                </div>
              </div>

              {showCommunicationEvent && (
                <div className="communication-event">
                  {recipientName && <span className="recipient-name">{recipientName}</span>}
                  {eventLabel && <span className="sent-label">{eventLabel}</span>}

                  {receivers.map(receiver => (
                    <Fragment key={receiver.id}>
                      <div className="grid-container">
                        <div className="left-section">
                          <Icon className={getReceiverStatusIcon(receiver.status)} />
                        </div>

                        <div className="right-section">
                          <span>
                            <span className="status-text">{getReceiverStatusText(receiver, t)}</span> {receiver.destination}
                          </span>
                        </div>
                      </div>

                      {receiver.status === COMMUNICATION_RECEIVER_STATUSES.Failed && receiver.reason && <span className="receiver-reason">{receiver.reason}</span>}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          ) : (
            status.name
          )}
        </div>
      }
      trigger={
        <div
          className={`StatusIcon ${disabled ? "disabled" : ""} ${active ? "active" : ""} ${onClick ? "" : "readonly"}`}
          style={{ backgroundColor, borderColor }}
          onClick={() => {
            if (!disabled && onClick) onClick();
          }}
        >
          <StatusIcon
            status={status.identifier}
            color={iconColor}
            appointment={{ id: statusIdentifier, customcom_status: customcomStatus, car_check_paused_at: checkPausedAt }}
            showSubIcon={false}
          />
        </div>
      }
    />
  );
};
